<template>
  <div class="template">
    <div class="fixed-header">
      <HeaderBar>
        <template #drawer-menu>
          <AcvButton
            variant="link"
            @click="handleDrawerToggle"
          >
            <img
              src="/images/dock_to_right.svg?url"
              alt="left drawer menu icon"
            />
          </AcvButton>
        </template>
        <template #header-title>
          {{ pageTitle }}
        </template>
      </HeaderBar>
    </div>
    <div class="template__body">
      <div class="template__side-left">
        <UIDrawer
          :menuOpen="isSideDrawerOpen"
          @toggle-menu="handleDrawerToggle"
          @toggle-overlay="handleDrawerToggle"
        >
          <span
            class="tw-pb-4 tw-text-sm tw-font-medium tw-uppercase tw-text-text-secondary tw-lg:tw-hidden"
          >
            Profile Settings
          </span>
          <nav class="template__link-list border-bottom">
            <ButtonNav :routes="carrierProfileRoutes" />
          </nav>
          <div class="account-rep">
            <p class="account-rep__heading">ACV Account Rep</p>
            <p class="account-rep__name">{{ amName }}</p>
            <div class="account-rep__phone">
              <ui-icon
                type="outlined"
                class="account-rep--icon"
                >phone</ui-icon
              >
              <p class="account-rep--text">(716) 235-1774</p>
            </div>
            <div class="account-rep__email">
              <ui-icon
                type="outlined"
                class="account-rep--icon"
                >email</ui-icon
              >
              <p class="account-rep--text">{{ amEmail }}</p>
            </div>
          </div>
        </UIDrawer>
      </div>

      <div class="template__side-right">
        <div class="tw-w-full tw-px-4 tw-pb-18 tw-md:tw-pr-[30px]">
          <Notification
            v-if="getActiveNotification?.value"
            :variant="getActiveNotification?.value?.variant"
            @dismissClicked="notificationsStore.popNotification(getActiveNotification?.key)"
          />
          <div
            class="wrapper tw-flex tw-w-full tw-items-baseline tw-justify-between tw-md:tw-pr-[30px]"
          >
            <h1>{{ pageTitle }}</h1>
            <slot name="action-button" />
          </div>
          <slot />
        </div>
      </div>
    </div>
  </div>

  <HeaderMobileNavBar v-if="isMobileScreen" />
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { carrierProfileRoutes } from '@/constants/carrierProfileRoutes';
import { useUserDetailsStore } from '@/stores/useUserDetails';
import { useNotificationsStore } from '@/stores/useNotifications';
import { useBreakpointHelper } from '@/composables/breakpointHelper';
import { useOverlayScrollHelper } from '@/composables/useOverlayScrollHelper';

const props = defineProps({
  pageTitle: {
    type: String,
    default: '',
  },
});

const { isBelowCurrentBreakpoint } = useBreakpointHelper();
const { preventBgFromScrolling, allowBgToScroll } = useOverlayScrollHelper();
const userDetailsStore = useUserDetailsStore();
const { amName, amEmail } = storeToRefs(userDetailsStore);

const isMobileScreen = computed(() => isBelowCurrentBreakpoint('tw-md'));

const isSideDrawerOpen = ref(false);

const notificationsStore = useNotificationsStore();
const { getActiveNotification } = storeToRefs(notificationsStore);

const handleDrawerToggle = () => {
  isSideDrawerOpen.value = !isSideDrawerOpen.value;
  // if drawer is open, prevent scroll
  isSideDrawerOpen.value ? preventBgFromScrolling() : allowBgToScroll();
};

onUnmounted(() => {
  allowBgToScroll();
});
</script>

<style lang="scss" scoped>
@use '@/assets/scss/container.scss';
@use '@/assets/scss/carrier-profile.scss';
@import '@/assets/scss/shared/_constants.scss';

.template__body {
  @apply tw-m-auto tw-h-full tw-pt-[53px];

  .template__side-left {
    left: -50px;
    @apply tw-fixed tw-z-10 tw-min-h-screen tw-bg-white;
    min-height: 100svh; // if browser doesn't support svh default to vh
    @media (min-width: $sm-min) {
      left: 0;
    }
  }
}

.border-bottom {
  @apply tw-border-[0] tw-border-b tw-border-solid tw-border-border-base;
}

.template__link-list {
  @apply tw-grow;

  @media (min-width: $md-min) {
    @apply tw-grow-0;
  }
}

.account-rep {
  @apply tw-py-8;

  @screen tw-lg {
    @apply tw-border-[0] tw-border-b tw-border-solid tw-border-border-base;
  }

  &__heading {
    @apply tw-mb-6 tw-text-sm tw-font-medium tw-uppercase tw-text-text-secondary;
  }
  &__phone,
  &__email {
    @apply tw-mb-1.5 tw-flex tw-flex-row tw-items-center;
  }

  &__name {
    @apply tw-text-base tw-font-medium tw-text-text-primary;
  }

  &--icon {
    @apply tw-rounded-full tw-bg-grey-light tw-p-2 tw-text-text-link-on-light;
  }

  &--text {
    @apply tw-ml-3 tw-text-sm tw-font-regular tw-text-text-primary;
  }
}

.template__side-right {
  @apply tw-max-w-screen-tw-3xl tw-pt-6 tw-md:tw-ml-12 tw-lg:tw-ml-[300px];

  :deep() {
    .notification {
      @apply tw-mb-6;
    }
  }
}

.fixed-header {
  :deep() {
    .tooltip {
      left: 5px;
      width: 300px;
      &::after {
        left: 18px;
      }

      @media (min-width: $sm-min) {
        @apply tw-hidden;
      }
    }
  }
}
</style>
